import {
  ActionIcon, Alert, Anchor, Badge, Button, Checkbox, Container, Fieldset, Grid, Group, Input, LoadingOverlay,
  Paper, SegmentedControl, Select, Stack, Tabs, Table, Text, TextInput, Tooltip,
  type ComboboxItem, type OptionsFilter,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import {
  IconAddressBook, IconAlertCircle, IconArrowBackUp, IconBuildingBank, IconClipboardList, IconEdit,
  IconFiles, IconLink, IconMapPin, IconPlus, IconRotate, IconShieldCheck, IconTrash, IconUnlink, IconUsers, IconX,
} from '@tabler/icons-react';
import * as React from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { useQuery, useQueryClient } from '@tanstack/react-query';

import { banksApi } from '../api/banks';
import { changeRequestsApi } from '../api/changeRequests';

import { useDirtyFormGuard, useFormSubmitSaver } from '../hooks/useDirtyFormGuard';
import { usePendingCreates } from '../hooks/usePendingCreates';
import { changedFieldLabels, fieldSuffix, labelizeField } from '../utils/toastFields';
import { diffUpdatePayload } from '../utils/diffPayload';
import { overlapConfirmProps, pendingFieldOverlap } from '../utils/pendingOverlap';
import { useAlertModal } from '../components/AlertModalProvider';

import { ApiError } from '../api/client';
import { applyApiErrorToForm, isFieldLevelApiError } from '../utils/applyApiError';
import type { AccountInput, AccountInsuranceInput, AccountPartyCreateInput, ChangeRequest, UUID } from '../api/types';
import { CLOSE_REASON_LABELS, PARTY_ROLE_LABELS } from '../api/types';
// PersonRef removed 2026-06-02 (Task #239) — Parties table now reads
// server-resolved person_display_name directly from AccountPartyOut.
import { AgreementRef, PersonRef } from '../components/EntityRef';
import { PendingCRBadge } from '../components/PendingCRBadge';
import { StatusExplained } from '../components/StatusExplained';
import { AgreementSummaryTable, InsurancePolicySummaryTable } from '../components/EntitySummary';
import { DisabledTooltipButton } from '../components/DisabledTooltipButton';
import { RowSeverIcon } from '../components/RowSeverIcon';
import { StickyDetailHeader } from '../components/StickyHeader';
import { SummaryItem } from '../components/SummaryBox';
import { AgreementPicker, InsurancePolicyPicker } from '../components/EntityPicker';
import {
  accountsKeys,
  useAccount, useAccountReferences, useCascadeDeleteAccount, useCreateAccount,
  useAttachAccountToAgreement,
  useDeleteAccount, useDetachAccountFromAgreement, useDetachAccountFromPolicy,
  useRestoreAccount, useUpdateAccount,
} from '../hooks/useAccounts';
import { useAgreement } from '../hooks/useAgreements';
import {
  useAccountInsurance, useAccountInsuranceList, useAttachAccountToPolicy,
  useCreateAccountInsurance,
} from '../hooks/useAccountInsurance';
import { useAccountParties, useCreateAccountParty, useDeleteAccountParty } from '../hooks/useAccountParties';
import { useConfirmDelete } from '../hooks/useConfirmDelete';
import { CascadeDeleteModal } from '../components/CascadeDeleteModal';
import { ReattachAccountModal } from '../components/ReattachAccountModal';
import type { NewPolicyDraft } from '../components/ReattachAccountModal';
import { AddAccountPartyModal } from '../components/AddAccountPartyModal';
import { useAddPartyModal, useNewPartyPersonResume } from '../components/PartyAddSteps';
import { IncompleteBanner } from '../components/IncompleteBanner';
import { FIELD_TABS, TabMissingCount, useMissingFields } from '../components/MissingFields';
import { DocumentsPanel } from '../components/DocumentsPanel';
import {
  documentResultParts, useDocumentStaging, type DocumentCommitResult,
} from '../hooks/useDocuments';

// Phase A3 (#412, 2026-06-10): Pending party-create op for staged-on-Save
// Add Person flow. Mirrors PendingPolicyPartyOp in AccountInsuranceDetailPage
// (#367) — single-kind union for now (only 'create'); Delete on row level is
// not in scope for #410's headline. tempKey uniquely identifies a pending
// row in the Parties table before it has a real DB id; failedPartyKeys
// references these tempKeys when a row's create fails mid-batch.
type PendingAccountPartyOp =
  | {
      kind: 'create';
      tempKey: string;
      payload: AccountPartyCreateInput;
      /** Captured at stage-time so the pending row renders the Person's
       *  display name without needing a per-row hook lookup. */
      person_display_name: string | null;
    }
  // #23 (2026-06-10, §3h sweep): per-row staged detach — mirrors the
  // Agreement Parties reference (#327 Phase 1). tempKey = party_id so
  // the failedKeys/Unstage machinery works unchanged for both kinds.
  | { kind: 'delete'; tempKey: string; party_id: UUID };

let _accountPartyTempKeySeq = 0;
function _nextAccountPartyTempKey(): string {
  _accountPartyTempKeySeq += 1;
  return `tmp-ap-${_accountPartyTempKeySeq}`;
}

interface FormValues {
  agreement_id: string;
  // Phase A2.b (2026-06-09): Indirect-creation support. Either agreement_id
  // OR opened_by_policy_id is required at create time, never both. The
  // SegmentedControl above the picker drives which one is populated.
  opened_by_policy_id: string;
  // The bank, from the Banks list (migration 0112). Its name, mnemonic, address,
  // SWIFT/BIC and contacts are the bank's (Admin → Banks), shown read-only here.
  bank_id: string;
  internal_num: string;
  rto_approved: boolean;
  iban: string;
  fund_number: string;
}

const EMPTY: FormValues = {
  agreement_id: '', opened_by_policy_id: '',
  bank_id: '', internal_num: '', rto_approved: false,
  iban: '', fund_number: '',
};

// #22 (2026-06-10): friendly names for the Save toast's changed-field
// suffix. Only fields whose name isn't derivable by the generic
// labelizeField humanizer need an entry (see utils/toastFields.ts).
const ACCOUNT_FIELD_LABELS: Record<string, string> = {
  agreement_id: 'Parent agreement',
  opened_by_policy_id: 'Parent policy',
  bank_id: 'Bank',
  internal_num: 'Internal number',
  fund_number: 'Fund number',
};

export function AccountDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isNew = !id || id === 'new';

  // Task #245 (2026-06-02): include_deleted=true so deleted accounts can
  // be viewed (DELETED state + Restore button on detail page).
  const { data, isLoading } = useAccount(isNew ? undefined : id, true);
  const create = useCreateAccount();
  const update = useUpdateAccount(id ?? '');
  const queryClient = useQueryClient();
  const del = useDeleteAccount();
  const cascadeDelete = useCascadeDeleteAccount();
  const refs = useAccountReferences(id, false);
  const [cascadeOpen, setCascadeOpen] = React.useState(false);
  // #404 (2026-06-09): Reattach modal state. Open with the target type
  // (Agreement or Policy); null = closed. Pattern B — the modal IS the
  // user's commit gesture; not staged on the Account's Edit transaction.
  const [reattachTarget, setReattachTarget] = React.useState<'agreement' | 'policy' | null>(null);
  const restore = useRestoreAccount();
  const confirmDelete = useConfirmDelete();

  // Phase A3.b (2026-06-09): parent-entity lookups for the header summary
  // box. Conditional — fires only when the relevant FK is set, so orphans
  // skip both queries and Direct/Indirect skip the one they don't need.
  // For Indirect accounts we use `effective_agreement_id` (the Policy's
  // owning Agreement, server-resolved) rather than `agreement_id` (which
  // is null on Indirect by schema). useAgreement / useAccountInsurance
  // already gate their queryFn on a truthy id via the `enabled` flag.
  const parentAgreementQ = useAgreement(data?.effective_agreement_id ?? undefined);
  const parentPolicyQ = useAccountInsurance(data?.opened_by_policy_id ?? undefined);

  const presetAgreementId = isNew ? (searchParams.get('agreement_id') ?? '') : '';
  // Phase A2.b (2026-06-09): Option B (#397) link from a Policy detail
  // page lands here with ?preset_policy_id=<uuid>. When set, the
  // SegmentedControl locks to "Policy (Indirect)" and the Policy
  // picker is pre-filled and disabled.
  const presetPolicyId = isNew ? (searchParams.get('preset_policy_id') ?? '') : '';

  // Determine parent type for the form. New-form: respect presets,
  // default to "agreement". Existing record: derive from data.
  const initialParentType: 'agreement' | 'policy' =
    isNew
      ? (presetPolicyId ? 'policy' : 'agreement')
      : (data?.opened_by_policy_id ? 'policy' : 'agreement');
  const [parentType, setParentType] = React.useState<'agreement' | 'policy'>(initialParentType);

  const initial: FormValues = isNew || !data ? {
    ...EMPTY,
    agreement_id: presetAgreementId,
    opened_by_policy_id: presetPolicyId,
  } : {
    // #273 (2026-06-08): Account.agreement_id became nullable post-0028
    // (Indirect Accounts and orphan/limbo Accounts have NULL agreement_id).
    // Coerce to '' so the form picker treats null as "unset" rather than
    // throwing on .trim() / value comparisons. The save path already
    // converts '' back to omit-or-null at the API boundary.
    agreement_id: data.agreement_id ?? '',
    opened_by_policy_id: data.opened_by_policy_id ?? '',
    bank_id: data.bank_id ?? '',
    // internal_num is nullable since migration 0019; coerce to '' for the
    // form so .trim() in validation doesn't crash on null.
    internal_num: data.internal_num ?? '',
    rto_approved: data.rto_approved,
    iban: data.iban ?? '',
    fund_number: data.fund_number ?? '',
  };

  const form = useForm<FormValues>({
    initialValues: initial,
    validate: {
      // Phase A2.b (2026-06-09): parent FK validation depends on which
      // branch the user is in.
      //
      // For isNew + parentType='agreement': agreement_id is required (32+ chars).
      // For isNew + parentType='policy':    opened_by_policy_id is required.
      // For existing records: never required — the existing parent FK is
      // disabled in the form, and the other branch is irrelevant.
      //
      // Closure note: `parentType` is captured by reference because
      // Mantine's useForm reads validate fresh on every form.onSubmit call.
      // Since useForm itself runs on every render, the latest closure is
      // always in effect.
      agreement_id: (v) => {
        if (!isNew || parentType !== 'agreement') return null;
        return v.length >= 32 ? null : 'Required (pick an agreement)';
      },
      opened_by_policy_id: (v) => {
        if (!isNew || parentType !== 'policy') return null;
        return v.length >= 32 ? null : 'Required (pick a policy)';
      },
      // A new account must choose its bank. An account from before the Banks list
      // without one can still be saved; the page asks for it (and the completeness
      // rule "A bank is chosen" lists it) until it is chosen.
      bank_id: (v) => (!isNew || v ? null : 'Choose the bank'),
      // internal_num is now optional (migration 0019). The completeness rule
      // 'Account / internal_num' surfaces missing values in the Compliance
      // dashboard's Incomplete Records tab. No client-side enforcement here.
    },
  });

  // The Banks list (migration 0112): the Mnemonic picker's options, and the chosen
  // bank's details (name, SWIFT/BIC, address, contacts), shown read-only.
  const banksQ = useQuery({ queryKey: ['banks'], queryFn: () => banksApi.list(), staleTime: 60_000 });
  const chosenBankId = form.values.bank_id || data?.bank_id || '';
  const bankQ = useQuery({
    queryKey: ['bank', chosenBankId], queryFn: () => banksApi.get(chosenBankId),
    enabled: !!chosenBankId, staleTime: 60_000,
  });
  const bank = bankQ.data;
  // Pedro's smoke (2026-10-20): the list reads "MNE (Bank name)"; once chosen, the box shows
  // the mnemonic only (the name is in the Bank box beside it). Search matches either.
  const bankOptions = (banksQ.data ?? []).map((b) => ({ value: b.id, label: b.mnemonic }));
  const bankNameById = Object.fromEntries((banksQ.data ?? []).map((b) => [b.id, b.name]));
  const bankFilter: OptionsFilter = ({ options, search }) => {
    const q = search.trim().toLowerCase();
    return (options as ComboboxItem[]).filter((o) =>
      !q || o.label.toLowerCase().includes(q) || (bankNameById[o.value] ?? '').toLowerCase().includes(q));
  };

  // #377 / A1.e (2026-06-09): Detach pending-op state. Pattern A on a
  // single-action surface: an Account can be Direct OR Indirect (mutually
  // exclusive by schema CHECK), so the staged detach is a single binary
  // discriminator rather than a list. Cleared on Cancel/Discard alongside
  // form.reset; committed on Save alongside the field update.
  type PendingDetach = null | 'from-agreement' | 'from-policy';
  const [pendingDetach, setPendingDetach] = React.useState<PendingDetach>(null);
  const detachFromAgreement = useDetachAccountFromAgreement();
  const detachFromPolicy = useDetachAccountFromPolicy();
  const [detachFailed, setDetachFailed] = React.useState(false);

  // Session A4 (old #416 / C-111 structural fix, 2026-06-10): Reattach is
  // now Pattern A — the modal STAGES the picked target; Save commits it.
  // Singleton like pendingDetach (an orphan can only reattach to one
  // parent). Cleared on Cancel/Discard; the C-111 zombie-Edit workaround
  // (modal onSuccess → setEditMode(false)) is obsolete on this path
  // because the Save handler owns the Edit-mode exit.
  // #18 (2026-06-10): third variant — create a NEW policy and attach this
  // (orphan) account to it, in one atomic backend write (account_ids[]
  // promotion side-channel; one CR covers both for RM filings).
  type PendingReattach = null
    | { target: 'agreement' | 'policy'; targetId: UUID }
    | { target: 'new-policy'; draft: NewPolicyDraft };
  const [pendingReattach, setPendingReattach] = React.useState<PendingReattach>(null);
  const attachToAgreement = useAttachAccountToAgreement();
  const attachToPolicy = useAttachAccountToPolicy();
  const [reattachFailed, setReattachFailed] = React.useState(false);

  // Phase A3 (#412, 2026-06-10): pending Add-Party ops staged on Save.
  // Mirrors AccountInsuranceDetailPage's pendingPartyOps shape (#367).
  // failedPartyKeys + failedPartyReasons track mid-batch failures by
  // tempKey so the Parties table can show a Failed badge + reason on
  // the affected row, leaving it in pendingPartyOps for retry-via-Save.
  const [pendingPartyOps, setPendingPartyOps] = React.useState<PendingAccountPartyOp[]>([]);
  const [failedPartyKeys, setFailedPartyKeys] = React.useState<Set<string>>(new Set());
  const [failedPartyReasons, setFailedPartyReasons] = React.useState<Map<string, string>>(new Map());
  const createParty = useCreateAccountParty();
  // #23 (2026-06-10): per-row staged detach commits via the same
  // maker-checker delete the party detail page uses (soft-delete of the
  // join row; the Person itself is untouched).
  const deleteParty = useDeleteAccountParty();
  // #18 (2026-06-10): create-new-policy-and-attach (staged on the Edit
  // transaction; commits on Save via the account_ids[] atomic promotion).
  const createPolicy = useCreateAccountInsurance();

  // #18 + #20 (2026-06-10): filed-awaiting-approval create-and-attach CRs
  // targeting this account (payload.account_ids contains it) render as a
  // blue phantom block on the Parent tab until Compliance decides.
  // Bug fix (2026-06-10, Pedro's smoke test 12): the router POPS
  // `account_ids` from the payload and stores it under the side-channel
  // key `_post_apply_orphan_account_ids` before filing the CR — matching
  // on 'account_ids' never hit, so the blue block silently never rendered.
  const filedPolicyCreates = usePendingCreates(
    'insurance_policies', '_post_apply_orphan_account_ids', isNew ? undefined : id, 'includes',
  );

  // Pedro's smoke tests 8/9/14 (2026-06-10): an RM-filed attach or detach
  // is an UPDATE CR on this account; the bare badge said "pending" but not
  // WHAT was pending — inviting duplicate or bifurcating requests. Fetch
  // the pending account-UPDATE CRs that touch the parent FKs and classify
  // them so the Parent tab can name the operation and preview the target.
  const pendingParentCRsQ = useQuery({
    queryKey: ['change-requests', 'pending-account-updates', id],
    queryFn: () => changeRequestsApi.list(
      { status: 'PENDING', entity_type: 'accounts' }, 200,
    ),
    enabled: !isNew && !!id,
  });
  type ParentChange = {
    cr: ChangeRequest;
    nature: 'attach-agreement' | 'attach-policy' | 'detach';
    targetId: string | null;
  };
  const pendingParentChanges = React.useMemo<ParentChange[]>(() => {
    return (pendingParentCRsQ.data?.items ?? [])
      .filter((cr) => cr.kind === 'UPDATE' && cr.entity_id === id)
      .flatMap((cr): ParentChange[] => {
        const p = cr.payload as {
          agreement_id?: string | null; opened_by_policy_id?: string | null;
        };
        const touchesParent =
          'agreement_id' in p || 'opened_by_policy_id' in p;
        if (!touchesParent) return [];
        if (p.agreement_id) {
          return [{ cr, nature: 'attach-agreement', targetId: p.agreement_id }];
        }
        if (p.opened_by_policy_id) {
          return [{ cr, nature: 'attach-policy', targetId: p.opened_by_policy_id }];
        }
        return [{ cr, nature: 'detach', targetId: null }];
      });
  }, [pendingParentCRsQ.data, id]);
  const pendingParentDetaches = pendingParentChanges.filter((c) => c.nature === 'detach');
  const pendingParentAttaches = pendingParentChanges.filter((c) => c.nature !== 'detach');

  // P23 (2026-10-10): documents on this account — staged in Edit mode, sent on Save.
  const docs = useDocumentStaging('accounts', isNew ? undefined : id);
  const pageDirty = form.isDirty() || pendingDetach !== null || pendingReattach !== null
    || pendingPartyOps.length > 0 || !docs.queue.isEmpty;
  // Pedro 2026-10-10: leaving with unsaved edits offers "Save & leave" (runs this page's Save).
  const saveForGuard = useFormSubmitSaver('account-form', pageDirty, !isNew);
  const confirmDiscard = useDirtyFormGuard(pageDirty, saveForGuard);
  const [editMode, setEditMode] = React.useState(isNew);
  const { showAlert, confirm } = useAlertModal();
  // #27 (2026-06-11, C-121 sweep): tabs become controlled so the
  // validation-failure handler can jump to the tab holding the error.
  const [activeTab, setActiveTab] = React.useState<string | null>('bank');
  // Pedro 2026-10-10: unfilled required fields (completeness rules) are outlined in
  // violet and counted on their tab; the banner's fields open their tab.
  const missing = useMissingFields('ACCOUNT', isNew ? undefined : data?.id);
  const hl = (f: keyof FormValues) => missing.input(f, form.values[f]);

  // P18 (2026-10-10): resume the "Create new person" round-trip, as on the
  // Agreement page. PersonDetailPage ferries the new person's id back as
  // ?new_party_person=<id>; this page keeps its tab in local state, so open the
  // Parties tab here, re-enter Edit mode (Add person is Edit-gated) and reopen
  // the Add-person modal on Attach with the person pre-selected. Waits for
  // `data` so the form is filled before Edit mode turns on.
  const [resumePartyPersonId, setResumePartyPersonId] = React.useState<string | null>(null);
  useNewPartyPersonResume(!!data, (pid) => {
    setActiveTab('parties');
    setResumePartyPersonId(pid);
    setEditMode(true);
  });

  // #31 (2026-06-11): hydration effect replaces the old render-time
  // sentinel guard (`form.values.bank_name === ''` → setValues), which
  // snapped the form back whenever the user CLEARED the bank name —
  // making the field impossible to empty (caught by Pedro in the #27
  // smoke). Hydrate when server data arrives or refetches, but never
  // while an edit is open. Pattern shared with the other detail pages.
  React.useEffect(() => {
    if (!isNew && data && !editMode) {
      form.setValues(initial);
      form.resetDirty(initial);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const _err = isNew ? create.error : update.error;
  const apiError = (_err instanceof ApiError && !isFieldLevelApiError(_err))
    ? _err.detail : null;
  const isDeleted = !!data?.deleted_at;

  async function handleSubmit(values: FormValues) {
    // Phase A2.b (2026-06-09): payload shape depends on parentType.
    // - Agreement branch: send agreement_id, omit opened_by_policy_id.
    // - Policy branch:    send opened_by_policy_id, omit agreement_id.
    // The backend's A2.a validation rejects both-FK-set with a friendly
    // 422; we keep the payload clean to avoid that path.
    const payload: AccountInput = {
      rto_approved: values.rto_approved,
    };
    if (values.bank_id) payload.bank_id = values.bank_id;
    if (parentType === 'agreement' && values.agreement_id) {
      payload.agreement_id = values.agreement_id;
    } else if (parentType === 'policy' && values.opened_by_policy_id) {
      payload.opened_by_policy_id = values.opened_by_policy_id;
    }
    // Bug fix (2026-06-09 during A1.f): internal_num was previously in
    // the unconditional payload, which shipped `internal_num: ''` when
    // the user didn't type one. The backend Pydantic schema
    // (account.py:26,50) accepts `str | None` BUT applies
    // `StringConstraints(min_length=1)` when a string IS provided —
    // empty string fails as `string_too_short` → HTTP 422. Move it into
    // the optional-when-non-empty loop alongside iban/swift_bic/contact_*
    // which already handle the empty-string case correctly.
    const optional: (keyof FormValues)[] = ['internal_num', 'iban', 'fund_number'];
    for (const k of optional) {
      const v = values[k];
      if (typeof v === 'string' && v !== '') (payload as Record<string, unknown>)[k] = v;
    }

    if (isNew) {
      create.mutate(payload, {
        onSuccess: (r) => {
          form.resetDirty();
          // Bug fix (2026-06-09 during A1.f): after create-then-navigate
          // (replace:true), the component re-renders with isNew=false
          // but useState(isNew) doesn't re-initialize, so editMode stays
          // true and the user sees Cancel/Save/Delete instead of Edit.
          // Flip editMode explicitly.
          setEditMode(false);
          if (r.pending) {
            showAlert({
              color: 'orange',
              title: 'Filed for compliance approval',
              message: 'Your new account was filed. The account is not yet visible — Compliance must approve it first.',
            });
            navigate(`/change-requests/${r.changeRequestId}`);
          } else {
            notifications.show({ message: 'Account created', color: 'green' });
            navigate(`/accounts/${r.entity.id}`, { replace: true });
          }
        },
        onError: (err) => { applyApiErrorToForm(form, err); },
      });
    } else {
      // #377 A1.e (2026-06-09): Pattern A — if a detach is staged we
      // commit field update first, then detach. Sequential rather than
      // parallel because the detach mutates `agreement_id` (or
      // `opened_by_policy_id`) on the server, and a parallel PATCH
      // carrying agreement_id from the form would race with the detach.
      // The detach endpoint is what produces the orphan state; the form
      // PATCH must not also set agreement_id when a detach is pending.
      //
      // #412 / A3.b (2026-06-10): pending Add-Party ops also commit on
      // Save. Order: field UPDATE → party CREATEs → detach (lifecycle).
      // Mirrors Phase 1's #372 ordering (relationship adds before
      // lifecycle changes). Party adds are independent of detach (parties
      // attach to the Account row directly, not via the parent
      // relationship), so detach producing the orphan state doesn't
      // affect the validity of just-added party rows.
      const hasFieldChanges = form.isDirty();
      // #22 (2026-06-10): Save toast names the changed fields, against the
      // pre-save baseline. Capped at 3 + "+N more" inside fieldSuffix.
      const changedLabels = changedFieldLabels(values, initial, ACCOUNT_FIELD_LABELS);
      // #36 (2026-06-11): UPDATE sends ONLY changed fields. The old
      // full payload always carried agreement_id, so derive_op_label
      // read every routine edit as "Attach to Agreement" on the pending
      // badge, the CR diff showed unchanged fields, and cleared
      // optionals were silently dropped (C-120 family). Parent FKs are
      // excluded outright — attach/detach ride their dedicated flows.
      const updatePayload = diffUpdatePayload(
        values as unknown as Record<string, unknown>,
        initial as unknown as Record<string, unknown>,
        {
          exclude: new Set(['agreement_id', 'opened_by_policy_id']),
          nullableOnClear: new Set(['internal_num', 'iban', 'fund_number']),
        },
      );
      // #37/old-#347 (2026-06-11, Pedro's decision: warn, don't block):
      // if a pending CR already changes any field in this payload, ask
      // before filing a competing change.
      if (hasFieldChanges) {
        const overlap = pendingFieldOverlap(updatePayload, data?.pending_change_requests ?? null);
        if (overlap) {
          const ok = await confirm(overlapConfirmProps(
            overlap, (f) => ACCOUNT_FIELD_LABELS[f] ?? labelizeField(f),
          ));
          if (!ok) return;
        }
      }
      const willDetach = pendingDetach !== null;
      const willReattach = pendingReattach !== null;
      // #23: party ops are now adds AND detaches — renamed accordingly.
      const willRunPartyOps = pendingPartyOps.length > 0;
      // P23: staged document adds / removes commit last (settled inside commit()).
      const willRunDocOps = !docs.queue.isEmpty;

      const runPartyOps = async () => {
        if (pendingPartyOps.length === 0) {
          return { applied: 0, pending: 0, failed: 0 };
        }
        let applied = 0;
        let pending = 0;
        let failed = 0;
        const nextFailedKeys = new Set<string>(failedPartyKeys);
        const nextFailedReasons = new Map<string, string>(failedPartyReasons);
        const survived: PendingAccountPartyOp[] = [];
        for (const op of pendingPartyOps) {
          try {
            // #23: branch per op kind — creates and detaches both flow
            // through maker-checker (RM filings produce pending CRs).
            const r = op.kind === 'create'
              ? await createParty.mutateAsync(op.payload)
              : await deleteParty.mutateAsync(op.party_id);
            if (r.pending) pending += 1; else applied += 1;
            // Success: drop the op from pending + clear any previous
            // failure state for this tempKey.
            nextFailedKeys.delete(op.tempKey);
            nextFailedReasons.delete(op.tempKey);
          } catch (err) {
            failed += 1;
            nextFailedKeys.add(op.tempKey);
            nextFailedReasons.set(op.tempKey,
              err instanceof ApiError ? err.detail : 'Unexpected error.');
            // Failed ops stay in pendingPartyOps for retry-via-Save.
            survived.push(op);
          }
        }
        setPendingPartyOps(survived);
        setFailedPartyKeys(nextFailedKeys);
        setFailedPartyReasons(nextFailedReasons);
        // #20 (2026-06-10): RM creates file PENDING CRs; refresh the CR
        // cache so the PartiesSection phantom row (usePendingCreates)
        // appears the moment the toast fires.
        if (pending > 0) {
          queryClient.invalidateQueries({ queryKey: ['change-requests'] });
        }
        return { applied, pending, failed };
      };

      const runDetach = async () => {
        if (!willDetach) return { applied: false, pending: false };
        try {
          const detachOp = pendingDetach === 'from-agreement'
            ? detachFromAgreement
            : detachFromPolicy;
          const result = await detachOp.mutateAsync(data!.id);
          // Smoke test 14 (2026-06-10): refresh the CR cache so the
          // "Pending approval · Detach" badge appears immediately.
          if (result.pending) {
            queryClient.invalidateQueries({ queryKey: ['change-requests'] });
          }
          setDetachFailed(false);
          setPendingDetach(null);
          return { applied: true, pending: result.pending };
        } catch {
          setDetachFailed(true);
          return { applied: false, pending: false };
        }
      };

      // Session A4 (old #416): staged reattach commits on Save, after the
      // field update. Mutually exclusive with detach in practice (detach
      // requires a parent; reattach requires orphan state) but ordered
      // after it defensively.
      // #18 (2026-06-10): third branch — create a NEW policy with
      // account_ids=[this account]; the backend atomically promotes the
      // orphan to Indirect under the same approval (one CR for RM).
      const runReattach = async (): Promise<{
        applied: boolean; pending: boolean; label?: string;
      }> => {
        if (!willReattach || !pendingReattach) return { applied: false, pending: false };
        try {
          if (pendingReattach.target === 'new-policy') {
            const d = pendingReattach.draft;
            const payload: AccountInsuranceInput = {
              agreement_id: d.agreement_id,
              insurer: d.insurer,
              policy_number: d.policy_number,
              account_ids: [data!.id],
            };
            if (d.fund_type) payload.fund_type = d.fund_type;
            if (d.fund_number) payload.fund_number = d.fund_number;
            const result = await createPolicy.mutateAsync(payload);
            // The create hook only invalidates the policy cache; the
            // account_ids[] promotion also mutates THIS account (orphan →
            // Indirect) server-side, so refresh the accounts cache too.
            queryClient.invalidateQueries({ queryKey: accountsKeys.all });
            // #20 rule: an RM filing renders as a blue phantom block on
            // the Parent tab — refresh the CR cache so it appears now.
            if (result.pending) {
              queryClient.invalidateQueries({ queryKey: ['change-requests'] });
            }
            setReattachFailed(false);
            setPendingReattach(null);
            return {
              applied: true, pending: result.pending,
              label: result.pending
                ? 'New policy + attach filed for approval — the Account stays orphan until Compliance approves'
                : 'Policy created · Account attached (now Active)',
            };
          }
          const result = pendingReattach.target === 'agreement'
            ? await attachToAgreement.mutateAsync({
                id: data!.id, agreementId: pendingReattach.targetId,
              })
            : await attachToPolicy.mutateAsync({
                policy_id: pendingReattach.targetId, account_id: data!.id,
              });
          // Smoke tests 8/9 (2026-06-10): refresh the CR cache so the
          // "Pending approval · Attach" preview appears immediately.
          if (result.pending) {
            queryClient.invalidateQueries({ queryKey: ['change-requests'] });
          }
          setReattachFailed(false);
          setPendingReattach(null);
          return { applied: true, pending: result.pending };
        } catch (err) {
          setReattachFailed(true);
          if (err instanceof ApiError) {
            showAlert({
              color: 'red',
              title: pendingReattach.target === 'new-policy'
                ? 'Create & attach failed'
                : 'Attach failed',
              message: err.detail,
            });
          }
          return { applied: false, pending: false };
        }
      };

      const composeToast = (
        fieldChanged: boolean,
        fieldPending: boolean,
        detachApplied: boolean,
        detachPending: boolean,
        partyApplied: number,
        partyPending: number,
        partyFailed: number,
        reattachApplied: boolean,
        reattachPending: boolean,
        docResult: DocumentCommitResult,
        // #18: the new-policy branch supplies its own wording.
        reattachLabel?: string,
      ) => {
        const parts: string[] = [];
        if (fieldChanged) {
          parts.push(fieldPending
            ? `Filed for approval${fieldSuffix(changedLabels)}`
            : `Saved${fieldSuffix(changedLabels)}`);
        }
        // #23: ops are now adds AND detaches — "change(s)" wording
        // mirrors AgreementDetailPage's settleSaveOutcome.
        if (partyApplied > 0) {
          parts.push(`${partyApplied} ${partyApplied === 1 ? 'Party change applied' : 'Party changes applied'}`);
        }
        if (partyPending > 0) {
          parts.push(`${partyPending} ${partyPending === 1 ? 'Party change filed for approval' : 'Party changes filed for approval'}`);
        }
        if (partyFailed > 0) {
          parts.push(`${partyFailed} ${partyFailed === 1 ? 'Party change failed' : 'Party changes failed'}`);
        }
        if (detachApplied) {
          parts.push(detachPending
            ? 'Detach filed for approval'
            : 'Account detached (now orphan)');
        }
        if (reattachApplied) {
          parts.push(reattachLabel ?? (reattachPending
            ? 'Attach filed for approval'
            : 'Account attached (now Active)'));
        }
        parts.push(...documentResultParts(docResult));
        if (parts.length === 0) return;
        const anyPending = fieldPending || detachPending || reattachPending || partyPending > 0
          || docResult.filed > 0;
        const anyFailed = partyFailed > 0 || docResult.failedKeys.size > 0;
        const color = anyFailed ? 'red' : (anyPending ? 'orange' : 'green');
        notifications.show({ color, message: parts.join(' · '), autoClose: 6000 });
      };

      if (hasFieldChanges) {
        update.mutate(updatePayload as AccountInput, {
          onSuccess: async (r) => {
            form.resetDirty(values);
            const partyResult = await runPartyOps();
            const detachResult = await runDetach();
            const reattachResult = await runReattach();
            const docResult = await docs.commit();
            // Exit Edit only if no failures across the batch. Use the
            // freshly-returned results rather than the closure-stale
            // detachFailed state variable.
            const detachFailedNow = willDetach && !detachResult.applied;
            const reattachFailedNow = willReattach && !reattachResult.applied;
            const anyFailed = detachFailedNow || reattachFailedNow || partyResult.failed > 0
              || docResult.failedKeys.size > 0;
            if (!anyFailed) setEditMode(false);
            composeToast(
              true, r.pending,
              detachResult.applied, detachResult.pending,
              partyResult.applied, partyResult.pending, partyResult.failed,
              reattachResult.applied, reattachResult.pending, docResult, reattachResult.label,
            );
          },
          onError: (err) => { applyApiErrorToForm(form, err); },
        });
      } else if (willDetach || willReattach || willRunPartyOps || willRunDocOps) {
        // No field changes — just commit staged party ops + lifecycle moves.
        (async () => {
          const partyResult = await runPartyOps();
          const detachResult = await runDetach();
          const reattachResult = await runReattach();
          const docResult = await docs.commit();
          const detachFailedNow = willDetach && !detachResult.applied;
          const reattachFailedNow = willReattach && !reattachResult.applied;
          const anyFailed = detachFailedNow || reattachFailedNow || partyResult.failed > 0
            || docResult.failedKeys.size > 0;
          const anyProgress = detachResult.applied || reattachResult.applied
            || partyResult.applied > 0 || partyResult.pending > 0
            || docResult.applied > 0 || docResult.filed > 0;
          if (!anyFailed && anyProgress) setEditMode(false);
          composeToast(
            false, false,
            detachResult.applied, detachResult.pending,
            partyResult.applied, partyResult.pending, partyResult.failed,
            reattachResult.applied, reattachResult.pending, docResult, reattachResult.label,
          );
        })();
      }
      // No-op if nothing pending (button is disabled).
    }
  }

  // Beta #11 cascade-delete (2026-05-26).
  const handleCascadeConfirm = (refIds: Record<string, string[]>) => {
    if (!data) return;
    cascadeDelete.mutate(
      { id: data.id, refIds: refIds as Record<string, string[]> },
      {
        onSuccess: () => { setCascadeOpen(false); navigate('/accounts'); },
        onError: async (err) => {
          if (err instanceof ApiError && err.code === 'cascade_refs_mismatch') {
            await refs.refetch();
            showAlert({
              color: 'orange',
              title: 'Attachments changed since you opened this dialog',
              message: err.detail + ' The list has been refreshed — please review and confirm again.',
            });
          } else {
            setCascadeOpen(false);
            showAlert({
              color: 'red',
              title: 'Cascade delete failed',
              message: err instanceof ApiError ? err.detail : 'Unexpected error.',
            });
          }
        },
      },
    );
  };

  return (
    <Container size="xl">
      <CascadeDeleteModal
        opened={cascadeOpen}
        onClose={() => setCascadeOpen(false)}
        entityNoun="Account"
        entityLabel={data ? `${data.bank_name ?? 'Account'} ${data.internal_num ?? ''}`.trim() : undefined}
        report={refs.data}
        onConfirm={handleCascadeConfirm}
        loading={cascadeDelete.isPending}
      />
      {/* Session A4 (old #416 / C-111 structural fix, 2026-06-10): the
          modal now STAGES the reattach via onStage — the page's Save
          Changes commits it alongside any other staged work, and the Save
          handler owns the Edit-mode exit. The Finding D workaround
          (onSuccess → setEditMode(false)) is gone with the root cause. */}
      {data && reattachTarget && (
        <ReattachAccountModal
          opened={reattachTarget !== null}
          onClose={() => setReattachTarget(null)}
          account={data}
          target={reattachTarget}
          onStage={(target, targetId) => {
            setPendingReattach({ target, targetId });
            setReattachFailed(false);
          }}
          // #18 (2026-06-10): create-new-policy-and-attach — staged like
          // any other reattach; Save commits both in one backend write.
          onStageCreateNew={(draft) => {
            setPendingReattach({ target: 'new-policy', draft });
            setReattachFailed(false);
          }}
        />
      )}
      <Stack>
        <StickyDetailHeader
          editing={editMode}
          title={isNew ? 'New account' : `${data?.bank_name ?? '...'} - ${data?.internal_num ?? ''}`}
          backOnClick={() => confirmDiscard(() => {
            // Bug fix (2026-06-09 during A1.f Test 6): clear all dirty
            // state BEFORE navigating so the passive useBlocker (from
            // DirtyFormProvider, #258) doesn't see lingering dirtiness
            // on the navigate and re-trigger the discard modal. Mirrors
            // the Cancel button's clearing logic below.
            // #412 / A3.b (2026-06-10): also clear pending party ops +
            // their failure state (same Edit-mode transaction).
            form.setValues(initial);
            form.resetDirty(initial);
            setPendingDetach(null);
            setDetachFailed(false);
            setPendingReattach(null);
            setReattachFailed(false);
            setPendingPartyOps([]);
            setFailedPartyKeys(new Set());
            setFailedPartyReasons(new Map());
            docs.queue.clear();
            setEditMode(false);
            navigate(-1);
          })}
          primary={
            // Task #245 (2026-06-02): hide Edit on deleted records.
            // #405 (2026-06-09): Closed accounts are terminal — show Edit
            // as greyed-with-tooltip per §3h corollary 3 rather than
            // hiding it. The user sees the affordance exists but learns
            // why it's unavailable. Closed status comes from cascade of
            // Policy Redeem/Lapse/Mature; the historical record is
            // intentionally immutable.
            !editMode && !isDeleted && data?.status === 'Closed' ? (
              <DisabledTooltipButton
                leftSection={<IconEdit size={16} />}
                disabledReason="This Account is Closed (terminal state via Policy termination cascade) and cannot be edited. The historical record is preserved as audit trail."
              >
                Edit
              </DisabledTooltipButton>
            ) : !editMode && !isDeleted ? (
              <Button leftSection={<IconEdit size={16} />} onClick={() => setEditMode(true)}>
                Edit
              </Button>
            ) : !editMode && isDeleted ? null : (
              <Group gap="xs">
                <Button variant="default" leftSection={<IconX size={16} />}
                  onClick={() => {
                    confirmDiscard(() => {
                      form.setValues(initial);
                      form.resetDirty(initial);
                      // #377 A1.e (2026-06-09): clear staged detach
                      // alongside the field reset — same Edit-mode
                      // transaction (§3g + Pattern A discipline).
                      setPendingDetach(null);
                      setDetachFailed(false);
                      // Session A4: staged reattach clears too.
                      setPendingReattach(null);
                      setReattachFailed(false);
                      // #412 / A3.b (2026-06-10): also clear pending
                      // party ops + their failure state.
                      setPendingPartyOps([]);
                      setFailedPartyKeys(new Set());
                      setFailedPartyReasons(new Map());
                      docs.queue.clear();
                      setEditMode(false);
                    });
                  }}>
                  Cancel
                </Button>
                <Button form="account-form" type="submit"
                  loading={create.isPending || update.isPending
                    || detachFromAgreement.isPending || detachFromPolicy.isPending
                    || attachToAgreement.isPending || attachToPolicy.isPending
                    || createParty.isPending}
                  // #377 A1.e (2026-06-09): enable Save when either field
                  // edits OR a staged detach are present (Pattern A).
                  // #412 / A3.b (2026-06-10): also enable when pending
                  // party ops are queued.
                  // Session A4: staged reattach enables Save too.
                  disabled={!form.isDirty() && !isNew && pendingDetach === null
                    && pendingReattach === null
                    && pendingPartyOps.length === 0 && docs.queue.isEmpty}>
                  {isNew ? 'Save' : 'Save changes'}
                </Button>
              </Group>
            )
          }
          destructive={
            // #7 (2026-06-16): entity Delete lives only on the entity-self
            // (Bank account) tab — on relationship tabs the only destructive
            // affordance is the per-row sever icon (§3h; matches the
            // AccountInsuranceDetailPage reference).
            activeTab !== 'bank' ? null
            : !isNew && data && !isDeleted ? (
              <DisabledTooltipButton variant="light" color="red" leftSection={<IconTrash size={16} />}
                disabledReason={editMode ? null : 'Enter Edit mode to delete'}
                enabledTooltip="Deletes this account record"
                onClick={async () => {
                  const result = await refs.refetch();
                  const report = result.data;
                  if (!report || !report.has_active_references) {
                    confirmDelete('account', () => del.mutate(data.id, {
                      onSuccess: () => navigate('/accounts'),
                      onError: (err) => showAlert({
                        color: 'red', title: 'Delete failed',
                        message: err instanceof ApiError ? err.detail : 'Unexpected error.',
                      }),
                    }));
                  } else {
                    setCascadeOpen(true);
                  }
                }}
                loading={del.isPending || refs.isFetching}>Delete</DisabledTooltipButton>
            ) : !isNew && data && isDeleted ? (
              <Button variant="light" color="green" leftSection={<IconRotate size={16} />}
                onClick={() => restore.mutate(data.id)} loading={restore.isPending}>Restore</Button>
            ) : null
          }
        />

        {/* Phase A3.b (2026-06-09): summary box mirrors the AgreementDetailPage
            pattern. Carries the parent-entity context (Agreement, Policy) as
            clickable links — the Agreement field is dimmed and annotated
            "via Policy" for Indirect accounts (where it's the policy's
            agreement, derived). Status badge anchors the right end. */}
        {!isNew && data && (() => {
          const isDirect = !!data.agreement_id;
          const isIndirect = !!data.opened_by_policy_id;
          const agreementLabel = parentAgreementQ.data
            ? `${parentAgreementQ.data.code_1} — ${parentAgreementQ.data.client_mnemonic}`
            : null;
          const policyLabel = parentPolicyQ.data
            ? `${parentPolicyQ.data.insurer} — ${parentPolicyQ.data.policy_number}`
            : null;
          const agreementTo = data.effective_agreement_id
            ? `/agreements/${data.effective_agreement_id}`
            : undefined;
          const policyTo = data.opened_by_policy_id
            ? `/account-insurance/${data.opened_by_policy_id}`
            : undefined;
          return (
            <Paper withBorder p="md" radius="sm">
              <Group justify="space-between" align="center" wrap="wrap">
                <Group gap="lg" wrap="wrap">
                  <SummaryItem label="Bank" value={data.bank_name} bold />
                  <SummaryItem label="Internal #" value={data.internal_num} mono />
                  <SummaryItem label="IBAN" value={data.iban} mono />
                  <SummaryItem
                    label="Agreement"
                    value={agreementLabel}
                    mono
                    to={agreementTo}
                    annotation={isIndirect && agreementLabel ? 'via Policy' : undefined}
                    muted={isIndirect}
                  />
                  <SummaryItem
                    label="Policy"
                    value={policyLabel}
                    to={policyTo}
                  />
                  {!isDirect && !isIndirect && (
                    <SummaryItem label="State" value="Orphan" />
                  )}
                </Group>
                {/* B1/#35 (2026-06-11, old #363): pending-CR state in the
                    header — visible from any tab, next to the status pill. */}
                <Group gap="xs" wrap="nowrap">
                <PendingCRBadge crs={data.pending_change_requests} />
                <StatusExplained entity="account" status={data.deleted_at ? 'Deleted' : data.status}>
                <Badge size="lg" variant="filled"
                  color={
                    data.deleted_at ? 'red'
                      : data.status === 'Active' ? 'green'
                      : data.status === 'Inactive' ? 'yellow'
                      : 'gray'  // Closed
                  }
                >
                  {data.deleted_at ? 'Deleted' : data.status}
                </Badge>
                </StatusExplained>
                </Group>
              </Group>
              {/* #405 (2026-06-09): why-Closed marker. Renders inside the
                  same Paper as the SummaryBox so the explanatory context
                  is colocated with the status badge. Only when status is
                  Closed; reads close_reason from the parent Policy (the
                  cascade source). The approximate date is the Policy's
                  updated_at — the redeem/lapse/mature endpoint touches
                  that on termination. */}
              {data.status === 'Closed' && (
                <Alert color="gray" variant="light" mt="xs"
                  icon={<IconAlertCircle size={16} />}>
                  <Text size="sm">
                    This Account is <Badge color="gray" variant="filled" size="sm">Closed</Badge>
                    {parentPolicyQ.data?.close_reason && (
                      <> via Policy {parentPolicyQ.data.insurer} — {parentPolicyQ.data.policy_number}
                        {' '}<Badge color="red" variant="outline" size="sm">
                          {CLOSE_REASON_LABELS[parentPolicyQ.data.close_reason]}
                        </Badge></>
                    )}
                    {parentPolicyQ.data?.updated_at && (
                      <> on {new Date(parentPolicyQ.data.updated_at).toLocaleDateString()}</>
                    )}
                    {!parentPolicyQ.data?.close_reason && (
                      <>. Termination context is no longer available (parent Policy linkage
                        may have been altered after closure).</>
                    )}.
                  </Text>
                  <Text size="xs" c="dimmed" mt={4}>
                    Closed accounts are terminal — they preserve the historical
                    record and cannot be edited or reactivated.
                  </Text>
                </Alert>
              )}
              {/* #406 (2026-06-09): why-Inactive marker for parent-cascade
                  Inactive. Distinguishes the scenario "this Account is
                  Inactive because its parent Agreement/Policy is Inactive"
                  (resume from parent) from orphan-Inactive (resume via
                  reattach affordances above). The dual-parent header
                  already shows the parent ref; this marker explains the
                  causal relationship.

                  Visible only when:
                    - Account.status === 'Inactive'
                    - Account has a parent FK set (isDirect or isIndirect)
                    - The relevant parent is itself Inactive

                  Skipped when the parent is Active (means the Account
                  was manually Inactivated independently — not a cascade)
                  or when the Account is orphan (no parent to point to). */}
              {data.status === 'Inactive' && (() => {
                const directParentInactive = !!data.agreement_id
                  && parentAgreementQ.data?.status === 'Inactive';
                const indirectParentInactive = !!data.opened_by_policy_id
                  && parentPolicyQ.data?.status === 'Inactive';
                if (!directParentInactive && !indirectParentInactive) return null;
                const parentLabel = directParentInactive ? 'Agreement' : 'Policy';
                return (
                  <Alert color="yellow" variant="light" mt="xs"
                    icon={<IconAlertCircle size={16} />}>
                    <Text size="sm">
                      This Account is{' '}
                      <Badge color="yellow" variant="filled" size="sm">Inactive</Badge>
                      {' '}because its {parentLabel} is Inactive.
                    </Text>
                    <Text size="xs" c="dimmed" mt={4}>
                      Cascade-inactivated Accounts cannot be reactivated
                      independently. Reactivate the parent {parentLabel} from
                      its own page to restore this Account to Active.
                    </Text>
                  </Alert>
                );
              })()}
            </Paper>
          );
        })()}

        {apiError && <Alert color="red" variant="light" icon={<IconAlertCircle size={16} />}>{apiError}</Alert>}

        {!isNew && data && <IncompleteBanner entity_type="ACCOUNT" id={data.id} onOpenTab={setActiveTab} />}

        {/* #27 (2026-06-11, C-121 sweep): Mantine runs validation BEFORE
            handleSubmit; a failing field on a hidden tab made Save appear
            dead. Second onSubmit argument names the fields and jumps to
            the owning tab. Reference: AgreementDetailPage. */}
        {/* noValidate (#27 round 2): inputs marked `required` trigger
            BROWSER-native validation, which intercepts the submit before
            React — visible field: native bubble (no tab jump); field on a
            hidden tab: unfocusable, submit silently blocked (the exact
            dead-Save mystery). All required fields have Mantine
            validators, so the app layer owns validation entirely. */}
        <form id="account-form" noValidate onSubmit={form.onSubmit(handleSubmit, (errors) => {
          const fields = Object.keys(errors);
          const FIELD_LABEL_OVERRIDES: Record<string, string> = {
            agreement_id: 'Parent Agreement', opened_by_policy_id: 'Parent Policy',
          };
          const labels = fields.map((f) =>
            FIELD_LABEL_OVERRIDES[f] ?? ACCOUNT_FIELD_LABELS[f] ?? f.replace(/_/g, ' '));
          // The one field → tab map (MissingFields.FIELD_TABS) holds the
          // validated fields too.
          setActiveTab(FIELD_TABS.ACCOUNT[fields[0]] ?? 'bank');
          showAlert({
            color: 'red',
            title: 'Cannot save — fix the highlighted field(s) first',
            message: `${labels.join(', ')}: ${fields.map((f) => String(errors[f])).join('; ')}`,
          });
        })}>
          <Stack pos="relative">
            <LoadingOverlay visible={isLoading} />
            <Tabs value={activeTab} onChange={setActiveTab} keepMounted>
              <Tabs.List>
                <Tabs.Tab value="bank" leftSection={<IconBuildingBank size={14} />}
                  rightSection={<TabMissingCount count={missing.tabCounts.bank} />}>Bank Acc.</Tabs.Tab>
                <Tabs.Tab value="address" leftSection={<IconMapPin size={14} />}
                  rightSection={<TabMissingCount count={missing.tabCounts.address} />}>Address</Tabs.Tab>
                <Tabs.Tab value="contact" leftSection={<IconAddressBook size={14} />}
                  rightSection={<TabMissingCount count={missing.tabCounts.contact} />}>Contact</Tabs.Tab>
                {!isNew && data && (
                  <Tabs.Tab value="parent" leftSection={<IconClipboardList size={14} />}>
                    {/* Phase A3.c (2026-06-09): static label. The tab content
                        below describes which parent type (Agreement, Policy,
                        or "No parent linked"). Replaces the dynamic
                        "Parent agreement" / "Parent policy" label from A1.e —
                        tab labels shouldn't shift on state. */}
                    Parent
                  </Tabs.Tab>
                )}
                {!isNew && data && <Tabs.Tab value="parties" leftSection={<IconUsers size={14} />}>Persons</Tabs.Tab>}
                {!isNew && data && <Tabs.Tab value="insurance" leftSection={<IconShieldCheck size={14} />}>Insurance</Tabs.Tab>}
                {!isNew && data && <Tabs.Tab value="documents" leftSection={<IconFiles size={14} />}>Documents</Tabs.Tab>}
              </Tabs.List>

              <Tabs.Panel value="bank" pt="md">
              <fieldset disabled={!editMode} style={{ border: 'none', padding: 0, margin: 0 }}>
                <Stack>
                  {/* Phase A2.b (2026-06-09): dual-parent picker. Segmented
                      control toggles between Agreement (Direct) and Policy
                      (Indirect) at create time. For existing records, the
                      branch is determined by data and the toggle is hidden
                      (you change parent via Detach + re-attach, not edit). */}
                  {/* Phase A2.b polish (2026-06-09): when the SegmentedControl
                      is still visible (isNew + no preset), the choice hasn't
                      been committed yet — show a neutral "Parent" title.
                      Once the choice is locked (preset OR existing record),
                      show the specific "Linked agreement" / "Linked policy". */}
                  <Fieldset legend={
                    isNew && !presetAgreementId && !presetPolicyId
                      ? 'Parent'
                      : parentType === 'policy' ? 'Linked policy' : 'Linked agreement'
                  }>
                    <Stack>
                      {isNew && !presetAgreementId && !presetPolicyId && (
                        <SegmentedControl
                          fullWidth
                          value={parentType}
                          onChange={(v) => {
                            const next = v as 'agreement' | 'policy';
                            setParentType(next);
                            // Clear the OTHER branch's FK when switching so
                            // the payload never carries both (the backend
                            // would reject with 422 anyway, but cleaner here).
                            if (next === 'agreement') form.setFieldValue('opened_by_policy_id', '');
                            else form.setFieldValue('agreement_id', '');
                          }}
                          data={[
                            { label: 'Agreement (Direct)', value: 'agreement' },
                            { label: 'Policy (Indirect)', value: 'policy' },
                          ]}
                        />
                      )}
                      {parentType === 'agreement' ? (
                        <AgreementPicker
                          label="Agreement"
                          required={isNew}
                          disabled={!isNew || !!presetAgreementId}
                          description={isNew && presetAgreementId
                            ? 'Locked — adding from this Agreement'
                            : 'The Agreement this Account belongs to. Search by code (e.g. CIIF-001) or by client_mnemonic.'}
                          value={form.values.agreement_id}
                          onChange={(v) => form.setFieldValue('agreement_id', v ?? '')}
                        />
                      ) : (
                        <InsurancePolicyPicker
                          label="Policy"
                          required={isNew}
                          disabled={!isNew || !!presetPolicyId}
                          description={isNew && presetPolicyId
                            ? 'Locked — adding from this Policy'
                            : 'The Insurance Policy this Account belongs to (Indirect). Search by insurer or policy number. Only Active policies are listed.'}
                          value={form.values.opened_by_policy_id}
                          onChange={(v) => form.setFieldValue('opened_by_policy_id', v ?? '')}
                        />
                      )}
                    </Stack>
                  </Fieldset>
                  <Fieldset legend="Bank account">
                    {/* Row 1: Bank mnemonic | Bank | Internal # | RTO. The bank comes from
                        the Banks list (Admin → Banks, migration 0112): picking its mnemonic
                        fills the name; renaming the bank there updates every account (the
                        mnemonic matches DIRECT accounts on fee import, C-229). */}
                    <Grid>
                      <Grid.Col span={3}>
                        <Select
                          label="Bank mnemonic" required={isNew} searchable allowDeselect={false}
                          placeholder={banksQ.isLoading ? 'Loading banks…' : 'Choose a bank'}
                          data={bankOptions} filter={bankFilter}
                          nothingFoundMessage="No bank: Admin adds banks in Admin → Banks"
                          renderOption={({ option }) => (
                            <Text size="sm"><b>{option.label}</b> ({bankNameById[option.value] ?? ''})</Text>
                          )}
                          {...form.getInputProps('bank_id')} {...hl('bank_id')}
                          value={form.values.bank_id || null}
                          onChange={(v) => form.setFieldValue('bank_id', v ?? '')}
                        />
                      </Grid.Col>
                      <Grid.Col span={3}>
                        <TextInput
                          label="Bank" readOnly variant="filled" tabIndex={-1}
                          value={bank?.name ?? (form.values.bank_id ? '' : (data?.bank_name ?? ''))}
                        />
                      </Grid.Col>
                      <Grid.Col span={3}><TextInput label="Internal account #" {...form.getInputProps('internal_num')} {...hl('internal_num')} /></Grid.Col>
                      <Grid.Col span={3}>
                        <Input.Wrapper label="RTO approved">
                          <Checkbox mt={6} {...form.getInputProps('rto_approved', { type: 'checkbox' })} />
                        </Input.Wrapper>
                      </Grid.Col>
                    </Grid>
                    <Grid mt="xs">
                      <Grid.Col span={8}><TextInput label="IBAN" maxLength={34} {...form.getInputProps('iban')} {...hl('iban')} /></Grid.Col>
                      <Grid.Col span={4}>
                        <TextInput label="SWIFT / BIC (the bank's)" readOnly variant="filled" tabIndex={-1}
                          value={bank?.swift_bic ?? ''} />
                      </Grid.Col>
                    </Grid>
                    {/* An account from before the Banks list with no bank chosen: said under
                        the row, not as a field description (which pushed the box out of line). */}
                    {!form.values.bank_id && data && !data.bank_id && (
                      <Text size="xs" c="orange" mt={4}>
                        This account's bank was typed before the Banks list ("{data.bank_name}"): choose its mnemonic.
                      </Text>
                    )}
                    <Grid mt="xs">
                      <Grid.Col span={8}>
                        {/* Indirect (via-policy) account fund — matches INDIRECT accounts
                            on fee import (C-229). Optional; blank for direct accounts.
                            Spaces + hyphens are stripped on input: the accountant col-I
                            carries the fund as e.g. "SAF 345678"; we store the canonical
                            space/hyphen-free form (SAF345678) so the import match is exact. */}
                        <TextInput
                          label="Insurance policy fund number"
                          description="Optional — indirect (via-policy) accounts only; used for fee-import matching. Spaces and hyphens are not allowed (e.g. enter SAF345678 for 'SAF 345678')."
                          {...form.getInputProps('fund_number')}
                          onChange={(e) => form.setFieldValue('fund_number', e.currentTarget.value.replace(/[-\s]/g, ''))}
                        />
                      </Grid.Col>
                    </Grid>
                  </Fieldset>
                </Stack>
                </fieldset>
              </Tabs.Panel>

              {/* The bank's address and contacts are the BANK's (Admin → Banks, migration
                  0112): every account of the bank shows the same, read-only here. */}
              <Tabs.Panel value="address" pt="md">
                <Fieldset legend="Bank address">
                  <Text size="sm">{bank?.address || (chosenBankId ? 'No address recorded for this bank.' : 'Choose the bank first.')}</Text>
                  <Text size="xs" c="dimmed" mt={6}>The bank's address, kept in Admin → Banks and shared by all its accounts.</Text>
                </Fieldset>
              </Tabs.Panel>

              <Tabs.Panel value="contact" pt="md">
                <Fieldset legend="Bank contacts">
                  {bank && bank.contacts.length > 0 ? (
                    <Table striped withTableBorder fz="sm">
                      <Table.Thead>
                        <Table.Tr><Table.Th>Name</Table.Th><Table.Th>Email</Table.Th><Table.Th>Phone</Table.Th>
                          <Table.Th>Mobile</Table.Th><Table.Th>Notes</Table.Th></Table.Tr>
                      </Table.Thead>
                      <Table.Tbody>
                        {bank.contacts.map((c) => (
                          <Table.Tr key={c.id}>
                            <Table.Td>{c.name ?? '-'}</Table.Td><Table.Td>{c.email ?? '-'}</Table.Td>
                            <Table.Td>{c.phone ?? '-'}</Table.Td><Table.Td>{c.mobile ?? '-'}</Table.Td>
                            <Table.Td>{c.notes ?? '-'}</Table.Td>
                          </Table.Tr>
                        ))}
                      </Table.Tbody>
                    </Table>
                  ) : (
                    <Text size="sm">{chosenBankId ? 'No contacts recorded for this bank.' : 'Choose the bank first.'}</Text>
                  )}
                  <Text size="xs" c="dimmed" mt={6}>The bank's contacts, kept in Admin → Banks and shared by all its accounts.</Text>
                </Fieldset>
              </Tabs.Panel>

              {!isNew && data && (
                <Tabs.Panel value="parent" pt="md">
              <fieldset disabled={!editMode} style={{ border: 'none', padding: 0, margin: 0 }}>
                  {/* #377 A1.e (2026-06-09): three-way render based on
                      Account ownership state:
                        • Direct  (agreement_id set, opened_by_policy_id null)
                          → AgreementSummaryTable + Detach-from-Agreement
                        • Indirect (opened_by_policy_id set, agreement_id null)
                          → InsurancePolicySummaryTable + Detach-from-Policy
                        • Orphan  (both null)
                          → "No parent linked" (already orphan; no detach)
                      Detach is Pattern A: clicking stages, Save commits via
                      maker-checker. Eligibility gating in DisabledTooltipButton
                      reasons mirrors §3h corollary 3. */}
                  {data.agreement_id ? (
                    <Stack gap="xs">
                      {/* Phase A3.c (2026-06-09): content heading describes
                          the parent type. Pairs with the static "Parent"
                          tab label.
                          C-117 polish (2026-06-10, Pedro's catch): staged
                          detach mirrors the staged-reattach preview — pill
                          beside the heading, Unstage top-right, table tinted
                          orange, caption below. Same layout grammar, inverse
                          semantics. */}
                      <Group justify="space-between" wrap="nowrap">
                        <Group gap="xs" wrap="nowrap">
                          <Text fw={600} size="sm" c="dimmed" tt="uppercase">Linked agreement</Text>
                          {/* Pedro's smoke test 14 (2026-06-10): the badge
                              names the pending operation so a filed detach
                              can't be mistaken for a filed attach. */}
                          {pendingParentDetaches.map((c) => (
                            <ParentPendingBadge key={c.cr.id} cr={c.cr} label="Detach" />
                          ))}
                          {pendingDetach === 'from-agreement' && (
                            <>
                              <Badge color="orange" variant="light" size="xs">Pending detach</Badge>
                              {detachFailed && (
                                <Tooltip
                                  label="This detach failed on the last Save — fix the cause and Save again, or unstage."
                                  withArrow>
                                  <Badge color="red" variant="light" size="xs">Failed</Badge>
                                </Tooltip>
                              )}
                            </>
                          )}
                        </Group>
                        <DetachStagingControls
                          flavour="from-agreement"
                          editMode={editMode}
                          accountStatus={data.status}
                          accountDeleted={!!data.deleted_at}
                          pendingDetach={pendingDetach}
                          onStage={() => setPendingDetach('from-agreement')}
                          onUnstage={() => { setPendingDetach(null); setDetachFailed(false); }}
                        />
                      </Group>
                      {pendingDetach === 'from-agreement' ? (
                        <>
                          <div style={{
                            backgroundColor: 'var(--mantine-color-orange-0)',
                            borderRadius: 8,
                            padding: 8,
                          }}>
                            <AgreementSummaryTable id={data.agreement_id} />
                          </div>
                          <Text size="xs" c="dimmed">
                            Detaches when you click Save Changes — this Account becomes an orphan.
                          </Text>
                        </>
                      ) : (
                        <AgreementSummaryTable id={data.agreement_id} />
                      )}
                    </Stack>
                  ) : data.opened_by_policy_id ? (
                    <Stack gap="xs">
                      <Group justify="space-between" wrap="nowrap">
                        <Group gap="xs" wrap="nowrap">
                          <Text fw={600} size="sm" c="dimmed" tt="uppercase">Linked policy</Text>
                          {/* Same nature-labeled badge as the agreement branch. */}
                          {pendingParentDetaches.map((c) => (
                            <ParentPendingBadge key={c.cr.id} cr={c.cr} label="Detach" />
                          ))}
                          {pendingDetach === 'from-policy' && (
                            <>
                              <Badge color="orange" variant="light" size="xs">Pending detach</Badge>
                              {detachFailed && (
                                <Tooltip
                                  label="This detach failed on the last Save — fix the cause and Save again, or unstage."
                                  withArrow>
                                  <Badge color="red" variant="light" size="xs">Failed</Badge>
                                </Tooltip>
                              )}
                            </>
                          )}
                        </Group>
                        <DetachStagingControls
                          flavour="from-policy"
                          editMode={editMode}
                          accountStatus={data.status}
                          accountDeleted={!!data.deleted_at}
                          pendingDetach={pendingDetach}
                          onStage={() => setPendingDetach('from-policy')}
                          onUnstage={() => { setPendingDetach(null); setDetachFailed(false); }}
                        />
                      </Group>
                      {pendingDetach === 'from-policy' ? (
                        <>
                          <div style={{
                            backgroundColor: 'var(--mantine-color-orange-0)',
                            borderRadius: 8,
                            padding: 8,
                          }}>
                            <InsurancePolicySummaryTable id={data.opened_by_policy_id} />
                          </div>
                          <Text size="xs" c="dimmed">
                            Detaches when you click Save Changes — this Account becomes an orphan.
                          </Text>
                        </>
                      ) : (
                        <InsurancePolicySummaryTable id={data.opened_by_policy_id} />
                      )}
                    </Stack>
                  ) : (
                    <Stack>
                      <Text fw={600} size="sm" c="dimmed" tt="uppercase">No parent linked</Text>
                      <Text c="dimmed" size="sm">
                        This Account is currently an orphan (Inactive, available
                        to attach to a new Agreement or Policy).
                      </Text>
                      {/* Session A4 (old #416): Reattach is now Pattern A —
                          the modal stages the target; Save commits. Buttons
                          are visible only in orphan state (§3h corollary 3)
                          and follow the C-110 gate direction: enabled in
                          Edit mode, greyed-with-reason in Read mode. While
                          a reattach is staged the buttons give way to the
                          staged banner below. */}
                      {data.status === 'Inactive' && !data.deleted_at && !pendingReattach && (
                        <Group justify="flex-start" mt="xs">
                          <DisabledTooltipButton
                            leftSection={<IconLink size={14} />}
                            variant="light"
                            color="green"
                            disabledReason={editMode ? null : 'Enter Edit mode to attach'}
                            onClick={() => setReattachTarget('agreement')}
                          >
                            Attach to Agreement
                          </DisabledTooltipButton>
                          <DisabledTooltipButton
                            leftSection={<IconLink size={14} />}
                            variant="light"
                            color="green"
                            disabledReason={editMode ? null : 'Enter Edit mode to attach'}
                            onClick={() => setReattachTarget('policy')}
                          >
                            {/* Pedro (2026-06-10): "Attach", not "Reattach" —
                                the policy flavour now also covers creating a
                                brand-new policy (#18). */}
                            Attach to a Policy
                          </DisabledTooltipButton>
                        </Group>
                      )}
                      {/* Session A4 + Pedro's Step-6 polish (2026-06-10):
                          the staged reattach previews in the SAME layout it
                          will have once committed — the future "Linked
                          agreement / Linked policy" heading and summary
                          table — tinted orange with a Pending Save pill and
                          an Unstage button. §3h: staged state mirrors
                          committed state so the user sees exactly what Save
                          will produce. */}
                      {pendingReattach && (
                        <Stack mt="xs" gap="xs">
                          <Group justify="space-between" wrap="nowrap">
                            <Group gap="xs" wrap="nowrap">
                              <Text fw={600} size="sm" c="dimmed" tt="uppercase">
                                {pendingReattach.target === 'agreement'
                                  ? 'Linked agreement' : 'Linked policy'}
                              </Text>
                              <Badge color="orange" variant="light" size="xs">
                                {pendingReattach.target === 'new-policy'
                                  ? 'Pending Save · create & attach' : 'Pending Save'}
                              </Badge>
                              {reattachFailed && (
                                <Tooltip
                                  label="This reattach failed on the last Save — fix the cause and Save again, or unstage."
                                  withArrow>
                                  <Badge color="red" variant="light" size="xs">Failed</Badge>
                                </Tooltip>
                              )}
                            </Group>
                            <Button size="xs" variant="light" color="gray"
                              onClick={() => { setPendingReattach(null); setReattachFailed(false); }}>
                              Unstage
                            </Button>
                          </Group>
                          <div style={{
                            backgroundColor: 'var(--mantine-color-orange-0)',
                            borderRadius: 8,
                            padding: 8,
                          }}>
                            {/* #18: positive `=== 'new-policy'` check first —
                                the first union member's target is itself a
                                two-literal union, so tsc won't eliminate it
                                through negative checks alone. */}
                            {pendingReattach.target === 'new-policy' ? (
                              // Retest 2 (2026-06-10): policy-centred
                              // preview mirroring the committed layout.
                              <DraftPolicyPreview
                                insurer={pendingReattach.draft.insurer}
                                policyNumber={pendingReattach.draft.policy_number}
                                fundType={pendingReattach.draft.fund_type}
                                fundNumber={pendingReattach.draft.fund_number}
                                agreementId={pendingReattach.draft.agreement_id}
                              />
                            ) : pendingReattach.target === 'agreement' ? (
                              <AgreementSummaryTable id={pendingReattach.targetId} />
                            ) : (
                              <InsurancePolicySummaryTable id={pendingReattach.targetId} />
                            )}
                          </div>
                          <Text size="xs" c="dimmed">
                            {pendingReattach.target === 'new-policy'
                              ? 'The Policy is created and this Account attached in one operation when you click Save Changes.'
                              : 'Commits when you click Save Changes.'}
                          </Text>
                        </Stack>
                      )}
                      {/* #18 + #20: filed-awaiting-approval create-and-attach
                          — blue phantom block (filed ≠ staged: blue vs
                          orange), badge links to the change request. Sourced
                          from pending insurance_policies CREATE CRs whose
                          account_ids contains this account. */}
                      {filedPolicyCreates.map((cr) => {
                        const p = cr.payload as {
                          insurer?: string; policy_number?: string;
                          fund_type?: string; fund_number?: string;
                          agreement_id?: string;
                        };
                        return (
                          <Stack mt="xs" gap="xs" key={cr.id}>
                            <Group gap="xs" wrap="nowrap">
                              <Text fw={600} size="sm" c="dimmed" tt="uppercase">Linked policy</Text>
                              <Tooltip
                                label="Filed for approval — Compliance must approve before the policy is created and this Account attached. Click to open the change request."
                                withArrow>
                                <Anchor component={Link} to={`/change-requests/${cr.id}`} size="sm">
                                  <Badge color="blue" variant="light" size="xs"
                                    styles={{ root: { cursor: 'pointer' } }}>
                                    Pending approval · create & attach
                                  </Badge>
                                </Anchor>
                              </Tooltip>
                            </Group>
                            <div style={{
                              backgroundColor: 'var(--mantine-color-blue-0)',
                              borderRadius: 8,
                              padding: 8,
                            }}>
                              {/* Retest 2 (2026-06-10): policy-centred
                                  preview mirroring the committed layout. */}
                              <DraftPolicyPreview
                                insurer={p.insurer ?? '—'}
                                policyNumber={p.policy_number ?? '—'}
                                fundType={p.fund_type}
                                fundNumber={p.fund_number}
                                agreementId={p.agreement_id ?? ''}
                              />
                            </div>
                            <Text size="xs" c="dimmed">
                              This Account stays orphan until Compliance approves.
                            </Text>
                          </Stack>
                        );
                      })}
                      {/* Pedro's smoke tests 8/9 (2026-06-10): a filed
                          attach-to-EXISTING (account UPDATE CR) renders the
                          same "ghost of Christmas future" preview as the
                          create-and-attach phantom above — target named,
                          badge links to the CR. */}
                      {pendingParentAttaches.map(({ cr, nature, targetId }) => (
                        <Stack mt="xs" gap="xs" key={cr.id}>
                          <Group gap="xs" wrap="nowrap">
                            <Text fw={600} size="sm" c="dimmed" tt="uppercase">
                              {nature === 'attach-agreement' ? 'Linked agreement' : 'Linked policy'}
                            </Text>
                            <ParentPendingBadge cr={cr} label="Attach" />
                          </Group>
                          <div style={{
                            backgroundColor: 'var(--mantine-color-blue-0)',
                            borderRadius: 8,
                            padding: 8,
                          }}>
                            {nature === 'attach-agreement' && targetId ? (
                              <AgreementSummaryTable id={targetId} />
                            ) : targetId ? (
                              <InsurancePolicySummaryTable id={targetId} />
                            ) : null}
                          </div>
                          <Text size="xs" c="dimmed">
                            This Account stays orphan until Compliance approves.
                          </Text>
                        </Stack>
                      ))}
                    </Stack>
                  )}
                  </fieldset>
                </Tabs.Panel>
              )}

              {!isNew && data && (
                <Tabs.Panel value="parties" pt="md">
              <fieldset disabled={!editMode} style={{ border: 'none', padding: 0, margin: 0 }}>
                  <PartiesSection
                    accountId={data.id}
                    accountLabel={`${data.bank_name ?? 'Account'} ${data.internal_num ?? ''}`.trim()}
                    editMode={editMode}
                    resumePersonId={resumePartyPersonId}
                    onResumeHandled={() => setResumePartyPersonId(null)}
                    pendingOps={pendingPartyOps}
                    failedKeys={failedPartyKeys}
                    failedReasons={failedPartyReasons}
                    onStageCreate={(payload, personDisplayName) => {
                      setPendingPartyOps((prev) => [
                        ...prev,
                        {
                          kind: 'create',
                          tempKey: _nextAccountPartyTempKey(),
                          payload,
                          person_display_name: personDisplayName,
                        },
                      ]);
                    }}
                    onStageDelete={(partyId) => {
                      // #23: same-key replace — staging a detach twice is
                      // a no-op (tempKey = party_id).
                      setPendingPartyOps((prev) => [
                        ...prev.filter((op) => op.tempKey !== partyId),
                        { kind: 'delete', tempKey: partyId, party_id: partyId },
                      ]);
                    }}
                    onUnstageOp={(tempKey) => {
                      setPendingPartyOps((prev) => prev.filter((op) => op.tempKey !== tempKey));
                      setFailedPartyKeys((prev) => {
                        const next = new Set(prev);
                        next.delete(tempKey);
                        return next;
                      });
                      setFailedPartyReasons((prev) => {
                        const next = new Map(prev);
                        next.delete(tempKey);
                        return next;
                      });
                    }}
                  />
                  </fieldset>
                </Tabs.Panel>
              )}

              {!isNew && data && (
                <Tabs.Panel value="insurance" pt="md">
              <fieldset disabled={!editMode} style={{ border: 'none', padding: 0, margin: 0 }}>
                  <InsuranceSection accountId={data.id} />
                  </fieldset>
                </Tabs.Panel>
              )}

              {/* P23 (2026-10-10): files on this account (e.g. IBAN proof). Not in a
                  disabled fieldset — View / Download work in Read mode. */}
              {!isNew && data && (
                <Tabs.Panel value="documents" pt="md">
                  <DocumentsPanel ownerType="accounts" ownerId={data.id}
                    editMode={editMode} staging={docs} />
                </Tabs.Panel>
              )}
            </Tabs>

            {!isNew && data && (
              <Text size="xs" c="dimmed" ta="right" mt="md">
                Last updated {new Date(data.updated_at).toLocaleString()}
              </Text>
            )}
          </Stack>
        </form>
      </Stack>
    </Container>
  );
}

const ACCOUNT_ROLE_COLOURS: Record<string, string> = {
  JOINT_HOLDER: 'teal', INSURANCE_BENEFICIARY: 'orange',
};

/**
 * Pedro's retest 2 (2026-06-10): the create-and-attach preview mirrors the
 * COMMITTED "Linked policy" layout — the policy is the table (Insurer /
 * Policy # / Fund / Status), the owning Agreement is the one-line caption.
 * The previous shape had the emphasis inverted (big Agreement table, small
 * policy footnote). §3h: staged/pending state mirrors committed state.
 */
function DraftPolicyPreview({ insurer, policyNumber, fundType, fundNumber, agreementId }: {
  insurer: string;
  policyNumber: string;
  fundType?: string;
  fundNumber?: string;
  agreementId: string;
}) {
  return (
    <Stack gap="xs">
      <Table withTableBorder>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Insurer</Table.Th>
            <Table.Th w={150}>Policy #</Table.Th>
            <Table.Th w={170}>Fund</Table.Th>
            <Table.Th w={150}>Status</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          <Table.Tr>
            <Table.Td><Text size="sm" fw={500}>{insurer}</Text></Table.Td>
            <Table.Td>{policyNumber}</Table.Td>
            <Table.Td>
              {fundType || '—'}{fundNumber ? ` #${fundNumber}` : ''}
            </Table.Td>
            <Table.Td>
              <Badge color="gray" variant="light">Will be created</Badge>
            </Table.Td>
          </Table.Tr>
        </Table.Tbody>
      </Table>
      <Group gap={6} wrap="nowrap">
        <Text size="xs" c="dimmed" tt="uppercase" fw={600}>Under agreement:</Text>
        <AgreementRef id={agreementId} linkPathPrefix="/agreements" />
      </Group>
    </Stack>
  );
}

/**
 * Pedro's smoke tests 8/9/14 (2026-06-10): nature-labeled pending badge for
 * Parent-tab filed changes. Blue = filed awaiting Compliance; the label
 * names the operation ("Attach" / "Detach") so two different pending
 * requests can't be confused at list level; clicking opens the CR.
 */
function ParentPendingBadge({ cr, label }: { cr: ChangeRequest; label: string }) {
  return (
    <Tooltip
      label="Filed for approval — Compliance must approve before it takes effect. Click to open the change request."
      withArrow>
      <Anchor component={Link} to={`/change-requests/${cr.id}`} size="sm">
        <Badge color="blue" variant="light" size="xs"
          styles={{ root: { cursor: 'pointer' } }}>
          Pending approval · {label}
        </Badge>
      </Anchor>
    </Tooltip>
  );
}

/**
 * #377 A1.e (2026-06-09): Pattern A detach affordance for AccountDetailPage's
 * Parent tab. Encapsulates the eligibility logic + DisabledTooltipButton
 * styling + Pending/Failed badges + Undo path. Used in two flavours:
 *   - 'from-agreement' (Direct → orphan via accountsApi.detachFromAgreement)
 *   - 'from-policy'    (Indirect → orphan via accountsApi.detachFromPolicy)
 *
 * Why both flavours share one component: the staging behaviour is identical
 * — only the eligibility check (which FK is set), the staged-flavour value,
 * and the button label differ. The commit path in handleSubmit picks the
 * mutation hook based on `pendingDetach`.
 */
interface DetachStagingControlsProps {
  flavour: 'from-agreement' | 'from-policy';
  editMode: boolean;
  accountStatus: string;
  accountDeleted: boolean;
  pendingDetach: null | 'from-agreement' | 'from-policy';
  onStage: () => void;
  onUnstage: () => void;
}

function DetachStagingControls({
  flavour, editMode, accountStatus, accountDeleted,
  pendingDetach, onStage, onUnstage,
}: DetachStagingControlsProps) {
  const isPending = pendingDetach === flavour;
  const label = flavour === 'from-agreement'
    ? 'Detach from Agreement'
    : 'Detach from Policy';

  // Eligibility (mirrors AgreementDetailPage AccountsSection: Active-only,
  // not deleted, in Edit mode).
  let disabledReason: string | null = null;
  if (!editMode) disabledReason = 'Enter Edit mode to detach';
  else if (accountDeleted) disabledReason = 'Deleted accounts cannot be detached';
  else if (accountStatus === 'Closed') disabledReason = 'Closed accounts cannot be detached';
  else if (accountStatus === 'Inactive') disabledReason = 'Inactive accounts cannot be detached';

  // #8 (2026-06-16, Pedro): cutting a tie = broken-link ActionIcon in line
  // with the record (matches the Parties/Roles row icons and the Agreement
  // Accounts-tab detach). Undo-arrow when staged; disabled icon with the
  // reason when ineligible. The Pending detach / Failed pills live beside
  // the section heading, rendered by the caller.
  if (isPending) {
    return (
      <Tooltip label="Unstage detach" withArrow position="left">
        <ActionIcon size="sm" variant="subtle" color="gray" onClick={onUnstage}>
          <IconArrowBackUp size={14} />
        </ActionIcon>
      </Tooltip>
    );
  }

  if (disabledReason) {
    return (
      <Tooltip label={disabledReason} withArrow position="left">
        <span style={{ display: 'inline-block' }}>
          <ActionIcon size="sm" variant="subtle" color="red" disabled>
            <IconUnlink size={14} />
          </ActionIcon>
        </span>
      </Tooltip>
    );
  }

  return (
    <Tooltip label={label} withArrow position="left">
      <ActionIcon size="sm" variant="subtle" color="red" onClick={onStage}>
        <IconUnlink size={14} />
      </ActionIcon>
    </Tooltip>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// PartiesSection — §3g Phase A3 (Task #413, 2026-06-10)
//
// Source-of-truth listing for account_parties on this account. Edit mode
// is page-level; in Edit mode an "+ Add person" button opens
// AddAccountPartyModal, which stages a CREATE op via onStageCreate.
// Pending rows render below DB rows with an orange "Pending Save" badge.
// The parent's Save Changes batches all pending ops through maker-checker.
// Mirrors PolicyPartiesSection on Insurance Policy detail (#369) — same
// hide-button-outside-Edit-mode behaviour (a §3h-vs-hide inconsistency
// already noted for #379's Phase A5 sweep; KEEP for cross-page consistency
// until the sweep aligns both pages together).
// ──────────────────────────────────────────────────────────────────────────
interface PartiesSectionProps {
  accountId: string;
  accountLabel: string;
  editMode: boolean;
  pendingOps: PendingAccountPartyOp[];
  failedKeys: ReadonlySet<string>;
  failedReasons: ReadonlyMap<string, string>;
  onStageCreate: (payload: AccountPartyCreateInput, personDisplayName: string | null) => void;
  /** #23: stage a per-row detach (commits on Save). */
  onStageDelete: (partyId: UUID) => void;
  onUnstageOp: (tempKey: string) => void;
  /** P18: a newly-created person's id ferried back from the "Create new
   *  person" round-trip — reopen the Add-person modal on Attach with it
   *  pre-selected. Cleared via onResumeHandled once consumed (one-shot). */
  resumePersonId?: string | null;
  onResumeHandled?: () => void;
}

function PartiesSection({
  accountId, accountLabel, editMode, pendingOps, failedKeys, failedReasons,
  onStageCreate, onStageDelete, onUnstageOp, resumePersonId, onResumeHandled,
}: PartiesSectionProps) {
  const partiesQ = useAccountParties({ account_id: accountId, limit: 200 });
  const returnTo = encodeURIComponent(`/accounts/${accountId}`);
  const partyRows = partiesQ.data?.items ?? [];
  const addModal = useAddPartyModal(resumePersonId, onResumeHandled);

  const pendingCreates = pendingOps.filter(
    (op): op is Extract<PendingAccountPartyOp, { kind: 'create' }> => op.kind === 'create',
  );
  // #23: rows with a staged detach get the strikethrough + Pending detach
  // treatment (mirrors Agreement Parties, #327 Phase 1).
  const pendingDeleteIds = new Set(
    pendingOps.filter((op) => op.kind === 'delete').map((op) => op.party_id),
  );

  // #20 (2026-06-10): Pedro's app-wide rule — filed-awaiting-approval
  // CREATEs render as phantom rows (blue treatment), distinct from the
  // orange staged-not-yet-saved rows above. See hooks/usePendingCreates.
  const filedCreates = usePendingCreates('account_parties', 'account_id', accountId);

  const hasRows = partyRows.length > 0 || pendingCreates.length > 0
    || filedCreates.length > 0;

  return (
    <Stack>
      {editMode && (
        <Group justify="flex-end">
          <Button size="xs" leftSection={<IconPlus size={14} />} variant="light"
            onClick={addModal.open}>
            Add person
          </Button>
        </Group>
      )}
      {partiesQ.isLoading && <Text c="dimmed" size="sm">Loading...</Text>}
      {!partiesQ.isLoading && !hasRows && (
        <Text c="dimmed" size="sm">
          No persons linked to this account yet — {editMode
            ? 'click "Add person" to link a joint holder or insurance beneficiary.'
            : 'enter Edit mode (button at top of page) to link a joint holder or insurance beneficiary.'}
        </Text>
      )}
      {hasRows && (
        <Table withTableBorder striped>
          <Table.Thead>
            <Table.Tr>
              <Table.Th w={180}>Role</Table.Th>
              <Table.Th>Person</Table.Th>
              <Table.Th w={200}>Status</Table.Th>
              <Table.Th w={100}></Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {partyRows.map((p) => {
              const pendingDelete = pendingDeleteIds.has(p.id);
              const failed = failedKeys.has(p.id);
              return (
              <Table.Tr key={p.id}
                // #5 (2026-06-16): opacity dims the row; line-through on Role
                // pill + Person cell only, status badges stay legible.
                style={pendingDelete ? { opacity: 0.6 } : undefined}>
                <Table.Td>
                  <Anchor component={Link} to={`/account-parties/${p.id}?return_to=${returnTo}`} size="sm" style={{ cursor: 'pointer' }}>
                    <Badge variant="light" color={ACCOUNT_ROLE_COLOURS[p.role]}
                      styles={{ root: { cursor: 'pointer' }, label: pendingDelete ? { textDecoration: 'line-through' } : undefined }}>
                      {PARTY_ROLE_LABELS[p.role] ?? p.role}
                    </Badge>
                  </Anchor>
                </Table.Td>
                {/* Task #239 (2026-06-02): server-resolved person_display_name
                    instead of <PersonRef> — drops the per-row /persons/<id>
                    cascade that previously fired here. Falls back to short-
                    UUID when the Person row is missing. */}
                <Table.Td style={pendingDelete ? { textDecoration: 'line-through' } : undefined}>
                  {p.person_display_name ? (
                    <Anchor component={Link} to={`/persons/${p.person_id}`} size="sm">
                      {p.person_display_name}
                    </Anchor>
                  ) : (
                    <Text size="xs" ff="monospace" c="dimmed">{p.person_id.slice(0, 8)}…</Text>
                  )}
                </Table.Td>
                <Table.Td>
                  <Stack gap={2} align="flex-start">
                    <Group gap={4} wrap="wrap">
                      <StatusExplained entity="party" status={p.deleted_at ? 'Deleted' : 'Active'}>
                      {p.deleted_at
                        ? <Badge color="gray" variant="light">Deleted</Badge>
                        : <Badge color="green" variant="light">Active</Badge>}
                      </StatusExplained>
                      {pendingDelete && <Badge color="orange" variant="light" size="xs">Pending detach</Badge>}
                      {failed && <Badge color="red" variant="filled" size="xs">Failed</Badge>}
                      {/* #23: RM-filed changes against this row (e.g. a
                          filed detach) stay visible after Save — Task 4
                          badge, same as Agreement Parties. */}
                      <PendingCRBadge crs={p.pending_change_requests} />
                    </Group>
                    {failed && failedReasons.get(p.id) && (
                      <Text size="xs" c="red">{failedReasons.get(p.id)}</Text>
                    )}
                  </Stack>
                </Table.Td>
                {/* #23 (2026-06-10): per-row staged detach — trash stages,
                    undo unstages; commits on Save. Soft-deleted rows hide
                    both affordances. Mirrors Agreement Parties. */}
                <Table.Td>
                  <RowSeverIcon
                    pending={pendingDelete}
                    editMode={editMode}
                    deleted={!!p.deleted_at}
                    onStage={() => onStageDelete(p.id)}
                    onUnstage={() => onUnstageOp(p.id)}
                    stageLabel="Detach this person (on Save)"
                  />
                </Table.Td>
              </Table.Tr>
              );
            })}
            {/* Pending CREATE rows — staged via the Add Person modal.
                Render below DB rows with an orange "Pending Save" badge
                so the user sees the new party will commit on Save Changes. */}
            {pendingCreates.map((op) => (
              <Table.Tr key={op.tempKey} style={{ backgroundColor: 'var(--mantine-color-orange-0)' }}>
                <Table.Td>
                  <Badge variant="light" color={ACCOUNT_ROLE_COLOURS[op.payload.role]}>
                    {PARTY_ROLE_LABELS[op.payload.role] ?? op.payload.role}
                  </Badge>
                </Table.Td>
                <Table.Td>
                  {op.person_display_name ? (
                    <Text size="sm">{op.person_display_name}</Text>
                  ) : (
                    <Text size="xs" ff="monospace" c="dimmed">
                      {(op.payload.person_id ?? '').slice(0, 8)}…
                    </Text>
                  )}
                </Table.Td>
                <Table.Td>
                  <Stack gap={2} align="flex-start">
                    <Group gap={4}>
                      <Badge color="orange" variant="light" size="sm">Pending Save</Badge>
                      {failedKeys.has(op.tempKey) && (
                        <Badge color="red" variant="filled" size="sm">Failed</Badge>
                      )}
                    </Group>
                    {failedKeys.has(op.tempKey) && failedReasons.get(op.tempKey) && (
                      <Text size="xs" c="red">
                        {failedReasons.get(op.tempKey)}
                      </Text>
                    )}
                  </Stack>
                </Table.Td>
                {editMode && (
                  <Table.Td>
                    {/* C-117: undo-arrow icon is THE row undo shape. */}
                    <Tooltip label="Unstage" withArrow>
                      <ActionIcon size="sm" variant="subtle" color="gray"
                        onClick={() => onUnstageOp(op.tempKey)}>
                        <IconArrowBackUp size={14} />
                      </ActionIcon>
                    </Tooltip>
                  </Table.Td>
                )}
              </Table.Tr>
            ))}
            {/* #20: filed-pending CREATE phantom rows — blue treatment
                ("filed, awaiting approval"), badge links to the change
                request. Sourced from pending CREATE CRs whose payload
                targets this account. */}
            {filedCreates.map((cr) => {
              const p = cr.payload as { person_id?: string; role?: string };
              return (
                <Table.Tr key={cr.id}
                  style={{ backgroundColor: 'var(--mantine-color-blue-0)' }}>
                  <Table.Td>
                    {p.role ? (
                      <Badge variant="light" color={ACCOUNT_ROLE_COLOURS[p.role] ?? 'gray'}>
                        {PARTY_ROLE_LABELS[p.role] ?? p.role}
                      </Badge>
                    ) : '—'}
                  </Table.Td>
                  <Table.Td>
                    <PersonRef id={p.person_id} linkPathPrefix="/persons" />
                  </Table.Td>
                  <Table.Td>
                    <Tooltip
                      label="Filed for approval — Compliance must approve before the party becomes active. Click to open the change request."
                      withArrow>
                      <Anchor component={Link} to={`/change-requests/${cr.id}`} size="sm">
                        <Badge color="blue" variant="light" size="xs"
                          styles={{ root: { cursor: 'pointer' } }}>
                          Pending approval
                        </Badge>
                      </Anchor>
                    </Tooltip>
                  </Table.Td>
                  {editMode && <Table.Td />}
                </Table.Tr>
              );
            })}
          </Table.Tbody>
        </Table>
      )}

      <AddAccountPartyModal
        opened={addModal.opened}
        onClose={addModal.close}
        accountId={accountId}
        accountLabel={accountLabel}
        initialPersonId={addModal.initialPersonId}
        onStage={(payload, personDisplayName) => {
          onStageCreate(payload, personDisplayName);
          addModal.close();
        }}
      />
    </Stack>
  );
}

function InsuranceSection({
  accountId,
}: {
  accountId: string;
}) {
  // Session A4 / old #418 (Pedro's decision, 2026-06-10): the
  // "+ Add insurance policy" button was REMOVED from this tab. It was a
  // pre-0028 leftover — it created an Agreement-level Policy from a child
  // record, and looked out of place next to the Parent tab's Attach
  // affordances, which are where Account↔Policy links are managed now.
  // Creating policies lives on the Agreement detail Insurance tab;
  // "create new policy and attach this account" is a planned extension of
  // the Parent tab's attach flow (see task ledger). This tab is read-only.
  // (Old #419 return-to-tab polish became moot with the removal.)
  const insuranceQ = useAccountInsuranceList({ account_id: accountId, limit: 200 });
  const returnTo = encodeURIComponent(`/accounts/${accountId}`);
  const insuranceRows = insuranceQ.data?.items ?? [];
  return (
    <Stack>
      {insuranceQ.isLoading && <Text c="dimmed" size="sm">Loading...</Text>}
      {!insuranceQ.isLoading && insuranceRows.length === 0 && (
        <Text c="dimmed" size="sm">No insurance policy linked to this account.</Text>
      )}
      {insuranceRows.length > 0 && (
        <Table withTableBorder striped>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Insurer</Table.Th>
              <Table.Th>Policy #</Table.Th>
              <Table.Th>Fund type</Table.Th>
              <Table.Th>Fund #</Table.Th>
              <Table.Th w={120}>Status</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {insuranceRows.map((p) => (
              <Table.Tr key={p.id}>
                <Table.Td>
                  <Anchor component={Link} to={`/account-insurance/${p.id}?return_to=${returnTo}`} size="sm" fw={500}>
                    {p.insurer}
                  </Anchor>
                </Table.Td>
                <Table.Td><Text ff="monospace" size="sm">{p.policy_number}</Text></Table.Td>
                <Table.Td><Text size="sm">{p.fund_type ?? '-'}</Text></Table.Td>
                <Table.Td><Text ff="monospace" size="sm">{p.fund_number ?? '-'}</Text></Table.Td>
                <Table.Td>
                  {/* P47: the policy's real status (this showed "Active" for every policy not
                      deleted, even an Inactive or Closed one), explained on hover. */}
                  <StatusExplained entity="policy" status={p.deleted_at ? 'Deleted' : p.status}>
                    {p.deleted_at
                      ? <Badge color="gray" variant="light">Deleted</Badge>
                      : <Badge variant="light"
                          color={p.status === 'Active' ? 'green' : p.status === 'Inactive' ? 'yellow' : 'red'}>
                          {p.status}
                        </Badge>}
                  </StatusExplained>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      )}
    </Stack>
  );
}
